import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usersApi } from '../../services/api/users.api';
import { User } from '../../types/user.response';
import { useClickOutside } from '../../hooks/useClickOutside';
import LogoutIcon from '../icons/LogoutIcon';
import SettingIcon from '../icons/SettingIcon';

interface ProfileDropdownProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ProfileDropdown({ isOpen, onClose }: ProfileDropdownProps) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuItemClassName =
        'flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900';

    useClickOutside(dropdownRef, onClose, isOpen);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userData = await usersApi.getUser();
                setUser(userData);
            } catch (err) {
                console.error('Error fetching user data:', err);
                localStorage.removeItem('token');
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchUserData();
        }
    }, [isOpen]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.reload();
    };

    if (!isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            className="w-[320px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg z-[1001]"
        >
            {loading ? (
                <div className="p-4 text-center">Loading...</div>
            ) : user && (
                <div className="divide-y divide-gray-100">
                    <div className="p-5">
                        <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#2563eb] text-base font-medium text-white">
                                {user.firstName?.charAt(0)}
                            </div>
                            <div>
                                <div className="font-medium text-gray-900">
                                    {user.firstName} {user.lastName}
                                </div>
                                <div className="mt-1 text-xs text-gray-400">Entry Year: {user.entryYear}</div>
                            </div>
                        </div>
                    </div>

                    <div className="p-2">
                        <button
                            onClick={handleLogout}
                            className={menuItemClassName}
                        >
                            <LogoutIcon className="h-5 w-5 text-gray-500" />
                            <span>Log out</span>
                        </button>
                        <Link
                            to="/settings"
                            onClick={onClose}
                            className={menuItemClassName}
                        >
                            <SettingIcon className="h-5 w-5 text-gray-500" />
                            <span>Settings</span>
                        </Link>
                    </div>
                    <div className="px-4 py-3 flex gap-3 text-xs text-gray-400">
                        <Link to="/privacy" onClick={onClose} className="hover:text-gray-600">Privacy Policy</Link>
                        <span>·</span>
                        <Link to="/terms" onClick={onClose} className="hover:text-gray-600">Terms of Service</Link>
                    </div>
                </div>
            )}
        </div>
    );
}
